import React from 'react';
import { Card, Tooltip } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const colorPalette = [
    '#e6f7ff', '#d9f7be', '#fffbe6', '#e6fffb', '#f9f0ff',
    '#ffccc7', '#fff1b8', '#d4e1f4', '#f6ffed', '#fff0f6'
];

const generateColor = (str) => {
    if (!str) return colorPalette[0];
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colorPalette.length;
    return colorPalette[index];
};

const getFacultyName = (facultyRef, facultyList) => {
    if (!facultyRef || !facultyList.length) return 'N/A';
    const faculty = facultyList.find(f => f._id === facultyRef);
    if (!faculty) return 'Faculty not found';
    return `${faculty.firstName || ''} ${faculty.lastName || ''}`.trim();
};

const getSubjectInfo = (subjectCode, subjectsList) => {
    if (!subjectCode || !subjectsList.length) return { code: subjectCode || 'N/A', name: 'N/A' };
    const subject = subjectsList.find(s => s.subjectCode === subjectCode);
    if (!subject) return { code: subjectCode, name: 'Subject not found' };
    return { code: subject.subjectCode, name: subject.subjectName };
};

const ScheduleVisualizer = ({ scheduleEntries, facultyList, subjectsList, onBlockClick, isViewMode }) => {
    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const timeSlots = [];
    for (let i = 7; i < 19; i++) { // 7 AM to 6:30 PM
        timeSlots.push(dayjs().hour(i).minute(0).format('HH:mm'));
        timeSlots.push(dayjs().hour(i).minute(30).format('HH:mm'));
    }

    const processedEntries = (scheduleEntries || [])
        .map((entry, index) => {
            if (!entry || !entry.timeStart || !entry.timeEnd || !entry.days || entry.days.length === 0) return null;
            const start = dayjs(entry.timeStart, 'HH:mm');
            const end = dayjs(entry.timeEnd, 'HH:mm');
            if (!start.isValid() || !end.isValid() || end.isSameOrBefore(start)) return null;

            const duration = end.diff(start, 'minute');
            const rowSpan = Math.ceil(duration / 30);
            const subjectInfo = getSubjectInfo(entry.subjectFormField, subjectsList);
            const facultyName = getFacultyName(entry.facultyRef, facultyList);
            const color = generateColor(entry.subjectFormField);

            return { ...entry, index, start, end, rowSpan, subjectInfo, facultyName, color };
        })
        .filter(Boolean);

    // This data structure will hold the grid data
    // Initialize a map for each time slot
    const gridData = new Map(timeSlots.map(time => [time, new Array(days.length).fill(null)]));

    // Populate the grid with entries
    processedEntries.forEach(entry => {
        entry.days.forEach(day => {
            const dayIndex = days.indexOf(day);
            if (dayIndex === -1) return; // Skip if day is not in our list

            const startTime = entry.start.format('HH:mm');
            if (gridData.has(startTime)) {
                // Place the entry in the correct day column
                gridData.get(startTime)[dayIndex] = entry;

                // Mark subsequent slots as occupied by this entry's rowspan
                for (let i = 1; i < entry.rowSpan; i++) {
                    const occupiedSlotTime = entry.start.add(i * 30, 'minutes').format('HH:mm');
                    if (gridData.has(occupiedSlotTime)) {
                        gridData.get(occupiedSlotTime)[dayIndex] = 'occupied';
                    }
                }
            }
        });
    });
    
    return (
        // --- FIXED --- Use `styles.body` instead of `bodyStyle`
        <Card title="Schedule Grid" styles={{ body: { padding: 0 } }}>
            <div style={{ overflowX: 'auto' }}>
                <table className="schedule-visualizer-table">
                    <thead>
                        <tr>
                            <th>Time</th>
                            {days.map(day => <th key={day}>{day}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {timeSlots.map(time => (
                            <tr key={time}>
                                <td>{dayjs(time, 'HH:mm').format('hh:mm A')}</td>
                                {days.map((day, dayIndex) => {
                                    const cellData = gridData.get(time)?.[dayIndex];

                                    // If cell is marked as 'occupied', don't render it
                                    if (cellData === 'occupied') {
                                        return null;
                                    }

                                    // If cell contains an entry object, render the block
                                    if (cellData && typeof cellData === 'object') {
                                        const entry = cellData;
                                        const blockStyle = {
                                            backgroundColor: entry.color,
                                            borderLeft: `5px solid ${dayjs(entry.color).subtract(20, 'l').format('hex')}`
                                        };

                                        return (
                                            <td
                                                key={`${day}-${time}`}
                                                rowSpan={entry.rowSpan}
                                                className={`schedule-block ${!isViewMode ? 'editable' : ''}`}
                                                style={blockStyle}
                                                onClick={() => !isViewMode && onBlockClick(entry.index)}
                                            >
                                                <Tooltip title={`${entry.subjectInfo.name} | ${entry.facultyName}`}>
                                                    <strong>{entry.subjectInfo.code}</strong>
                                                    <div>Room: {entry.room || 'N/A'}</div>
                                                    <div>{entry.facultyName}</div>
                                                </Tooltip>
                                            </td>
                                        );
                                    }
                                    
                                    // Otherwise, render an empty cell
                                    return <td key={`${day}-${time}`}></td>;
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};

export default ScheduleVisualizer;